import type { GetApiUrlParams } from "@/base/get-api-url"
import type { TargetDatabaseName } from "@/base/types"

import * as ExpoApplication from "expo-application"
import Constants from "expo-constants"
import { getCalendars, getLocales } from "expo-localization"
import * as Updates from "expo-updates"
import { Platform } from "react-native"
import * as Overrides from "@/base/overrides"
import { EnvironmentConfiguration } from "app.config"

let isEndToEndTest = false
if (Platform.OS === "web" && typeof window !== "undefined") {
  isEndToEndTest = Boolean(window.Cypress)
}

const _appConfigEnvironment: EnvironmentConfiguration = Constants.expoConfig?.extra?.environment

let targetDatabaseName: TargetDatabaseName = (
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  Overrides.EXPO_PUBLIC_TARGET_DATABASE_NAME
  || _appConfigEnvironment?.EXPO_PUBLIC_TARGET_DATABASE_NAME
)

if(typeof jest !== 'undefined'){
  targetDatabaseName = "LOCAL"
}


if (typeof targetDatabaseName === "undefined") {
  throw new TypeError("targetDatabaseName is undefined")
}

let apiUrl: string = (
  Overrides.API_URL
  || _appConfigEnvironment?.API_URL
)

let webappBaseUrl: string = (
  Overrides.WEBAPP_BASE_URL
  || _appConfigEnvironment?.WEBAPP_BASE_URL
)

const LOCALE_INFO = getLocales()[0]
const APP_VERSION =
  ExpoApplication.nativeBuildVersion ??
  Constants.expoConfig?.version ??
  "unknown"


function validateE2EStartCondition(isE2E: boolean, targetDb: string, _apiUrl: string): void {
  if(!isE2E){
    return
  }

  const isPointedAtUserEnv: boolean = (
    (targetDb === "PROD" || targetDb === "BETA")
    || ( _apiUrl.includes("BETA") || _apiUrl.includes("PROD") )
  )

  if(isPointedAtUserEnv){
    throw new TypeError(`E2E tests are pointing at non-nullable deployment ${targetDb} and url ${_apiUrl}.`)
  }
}

validateE2EStartCondition(isEndToEndTest, targetDatabaseName, apiUrl)

const segmentWriteKey = _appConfigEnvironment?.EXPO_PUBLIC_SEGMENT_WRITE_KEY
const sentryDsn = _appConfigEnvironment?.EXPO_PUBLIC_SENTRY_DSN
const googleMapsApiKey = _appConfigEnvironment?.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY
const buildTimestamp = _appConfigEnvironment?.BUILD_TIMESTAMP
const gitCommitSha = _appConfigEnvironment?.GIT_COMMIT_SHA ?? "unknown"

export const ENVIRONMENT = {
  apiUrl,
  appBuildNumber: Constants.expoConfig?.version,
  appName: Constants.expoConfig?.extra?.APP_NAME,
  appVersion: APP_VERSION,
  applicationId: ExpoApplication.applicationId ?? "id-unknown",
  buildTimestamp,
  gitCommitSha,
  googleMapsApiKey,
  isEndToEndTest,
  locale: { ...LOCALE_INFO },
  localhost: `localhost`,
  releaseChannel: Updates.channel,
  segmentWriteKey,
  sentryDsn,
  targetDatabaseName,
  updateId: Updates.updateId,
  webappBaseUrl,
}
ENVIRONMENT satisfies GetApiUrlParams

export function getTimezoneForUser() {
  return getCalendars()[0]?.timeZone
}

export type AppEnvironment = typeof ENVIRONMENT

/**
 * Check if the target database matches any of the given names.
 * This helper avoids TypeScript narrowing issues when the build-time type
 * is narrowed to a single literal (e.g., "LOCAL" in local builds).
 */
export function isTargetDatabase(...names: TargetDatabaseName[]): boolean {
  return names.includes(ENVIRONMENT.targetDatabaseName as TargetDatabaseName)
}

/**
 * We should never attempt to seed the database in production or beta
 * (demo mode works differently)
 */
export function canSeedTestDatabase(targetDb: string) {
  return ["LOCAL", "DEV"].includes(targetDb)
}

if(["LOCAL", "DEV"].includes(targetDatabaseName)){
  console.log("Runtime ENV:", ENVIRONMENT);
}
